import { useRef, type ReactNode } from 'react';

// A compact button-group toggle (e.g. degrees/radians, percentage/absolute) — distinct from
// RadioGroup's vertically-stacked circle-radio list, which is a different visual language for a
// different context (a longer list of mutually-exclusive options, not a 2-3-way inline switch).
// Originally lived private to numerical-block.tsx (its degrees/radians angle-mode switch);
// extracted here once dropdown-block.tsx needed the identical control for its own angle-mode
// toggle, so both share one implementation rather than drifting into two near-identical copies.
//
// A11y: it's a single-select, so it's a WAI-ARIA `radiogroup` — each option is a `radio` with
// `aria-checked`, roving tabindex (only the selected option is in the tab order), and Arrow keys
// move + select (Home/End jump to the ends). Enter/Space select via the native button click.
export function SegmentedControl({
  options, value, onChange, 'aria-label': ariaLabel, className, stretch, disabled, fieldId,
}: {
  /** `ariaLabel` per option, for when the visible label is a bare symbol (=, <, between) whose
   * meaning doesn't survive being read aloud on its own. */
  options: { id: string; label: string; icon?: ReactNode; ariaLabel?: string }[];
  value: string;
  onChange: (v: string) => void;
  'aria-label'?: string;
  className?: string;
  /** Segments share the full width instead of sizing to their own text. Off by default so callers
   * keep their shrink-to-fit look; Compass/Angle's condition-type switch needs it on. */
  stretch?: boolean;
  /** The whole switch is unavailable — greyed and inert, never removed, matching how RadioGroup and
   * CheckboxRow already handle an option that can't currently apply. Whole-control rather than
   * per-segment because a segmented switch is one choice: if it can't be made, none of its segments
   * can. (GPS/Compass use this when Capture on Submit is selected.) */
  disabled?: boolean;
  /** Names the data key this control writes, so an undo can pulse the whole control rather than
   * the whole block. A mode switch is one choice, so the tag belongs on the group, not a segment. */
  fieldId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const idx = options.findIndex((o) => o.id === value);
  const selectAt = (i: number) => {
    const opt = options[(i + options.length) % options.length];
    if (!opt) return;
    onChange(opt.id);
    // Move focus to the newly-selected radio (roving tabindex will make it the tabbable one).
    ref.current?.querySelectorAll<HTMLButtonElement>('button')[(i + options.length) % options.length]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const from = idx < 0 ? 0 : idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); selectAt(from + 1); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); selectAt(from - 1); }
    else if (e.key === 'Home') { e.preventDefault(); selectAt(0); }
    else if (e.key === 'End') { e.preventDefault(); selectAt(options.length - 1); }
  };
  return (
    <div ref={ref} role='radiogroup' aria-label={ariaLabel} data-field-id={fieldId} tabIndex={-1} onKeyDown={onKeyDown}
      className={`flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5 ${disabled ? 'opacity-40' : ''} ${className ?? ''}`}>
      {options.map((opt, i) => {
        const checked = value === opt.id;
        return (
          <button
            key={opt.id}
            type='button'
            role='radio'
            aria-checked={checked}
            aria-label={opt.ariaLabel}
            disabled={disabled}
            // Roving tabindex: only the selected option (or the first, if none is selected) is tabbable.
            tabIndex={checked || (idx < 0 && i === 0) ? 0 : -1}
            onClick={() => onChange(opt.id)}
            // `whitespace-nowrap` so a segment states the width it needs instead of wrapping to
            // fit whatever it is given. A segmented switch is read as one row of alternatives,
            // and "NE 45°" broken over two lines stops looking like one. It also lets a caller
            // MEASURE the control: the options panel sizes its columns from `max-content`, and
            // a wrapping label reports a max-content width far narrower than it really wants,
            // so the column came out too tight and the label wrapped anyway.
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs transition-colors disabled:cursor-not-allowed ${stretch ? 'flex-1 justify-center' : ''} ${
              checked ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {opt.icon}{opt.label}
          </button>
        );
      })}
    </div>
  );
}
